/**
 * Skill tree domain: specs, formulas, and canLearn check.
 * Source: SelectorPopulator.as, SelectorCore.as (AS3 canonical data).
 */

export const SkillId = {
  MORE_INITIAL_MANA: 0,
  MANA_REPLENISH: 1,
  BANISH_MANA_COST: 2,
  LOWER_CREATING_AND_COMBINING_MANA_COST: 3,
  BUILDING_COST: 4,
  DEMOLITION: 5,
  POISON_MASTERY: 6,
  BLOODBOUND_MASTERY: 7,
  VIOLENT_EXPLOSIONS: 8,
  MULTIPLE_DAMAGE_MASTERY: 9,
  CHAIN_HIT_MASTERY: 10,
  AMPLIFIER_POWER: 11,
  CHEAPER_MANA_POOL_AND_UNLOCK: 12,
  DUAL_GEM_MASTERY: 13,
  TRIPLE_GEM_MASTERY: 14,
  SLOW_MASTERY: 15,
  ARMOR_TEARING_MASTERY: 16,
  SHRINES: 17,
  SHOCKING_MASTERY: 18,
  MANA_GATHERING_MASTERY: 19,
  TRAPS: 20,
  DAMAGE: 21,
  WILD_STARTER_GEM: 22,
  GEM_DEPLOYMENT_TIME: 23,
  FIRING_SPEED: 24,
  RANGE: 25,
  BADGES_TO_MANA: 26,
} as const;

export type SkillId = (typeof SkillId)[keyof typeof SkillId];

export interface SkillSpec {
  id: number;
  name: string;
  maxLevel: number;
  firstLevelCost: number;
  maxLevelCost: number;
  playerLevelReq: number;
  firstLevelValue: number;
  maxLevelValue: number;
}

// AS3 source arrays (SelectorPopulator.as)
const skillMaxLevels     = [20, 50, 30, 30, 20, 10, 20, 20, 15, 20, 20, 30, 20, 10, 12, 20, 20, 20, 20, 20, 15, 20, 20, 20, 20, 20, 20];
const skillFirstLevelCosts    = [ 1,  1,  2,  1,  1,  2,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1,  1, 4.5, 1,  1, 3.2,  3];
const skillMaxLevelCosts      = [ 4, 4.5, 4,  7, 3.5, 8,  3, 2.5, 5,  3,  3, 3.5, 2,  4, 6.5, 3,  3,  4, 3.2, 3.5, 2.8, 5.6, 10.9, 2.5, 7.7, 9.5, 5];
const skillPlayerLevelReqs    = [ 0,  0,  0,  5,  5,  0, 11, 11,  0, 17, 17,  0, 23, 23,  0, 29, 29,  0, 35, 35,  0,  0, 41,  0,  0,  0,  0];
const skillFirstLevelValues   = [16,  4,  4,  2,  4,  1,  4,  5,  1,  6,  4,  1,  1,  1,  1,  4,  2,  3,  3,  3,  1,  1,  2,  4,  3,  1, 25];
const skillMaxLevelValues     = [160, 50, 50, 30, 40, 10, 80, 100, 15, 80, 50, 30, 20, 10, 12, 60, 40, 50, 40, 40, 30, 30, 21, 80, 40, 20,  5];

const SKILL_NAMES: string[] = [
  "MORE_INITIAL_MANA",
  "MANA_REPLENISH",
  "BANISH_MANA_COST",
  "LOWER_CREATING_AND_COMBINING_MANA_COST",
  "BUILDING_COST",
  "DEMOLITION",
  "POISON_MASTERY",
  "BLOODBOUND_MASTERY",
  "VIOLENT_EXPLOSIONS",
  "MULTIPLE_DAMAGE_MASTERY",
  "CHAIN_HIT_MASTERY",
  "AMPLIFIER_POWER",
  "CHEAPER_MANA_POOL_AND_UNLOCK",
  "DUAL_GEM_MASTERY",
  "TRIPLE_GEM_MASTERY",
  "SLOW_MASTERY",
  "ARMOR_TEARING_MASTERY",
  "SHRINES",
  "SHOCKING_MASTERY",
  "MANA_GATHERING_MASTERY",
  "TRAPS",
  "DAMAGE",
  "WILD_STARTER_GEM",
  "GEM_DEPLOYMENT_TIME",
  "FIRING_SPEED",
  "RANGE",
  "BADGES_TO_MANA",
];

export const SKILL_SPECS: SkillSpec[] = Array.from({ length: 27 }, (_, i) => ({
  id: i,
  name: SKILL_NAMES[i],
  maxLevel: skillMaxLevels[i],
  firstLevelCost: skillFirstLevelCosts[i],
  maxLevelCost: skillMaxLevelCosts[i],
  playerLevelReq: skillPlayerLevelReqs[i],
  firstLevelValue: skillFirstLevelValues[i],
  maxLevelValue: skillMaxLevelValues[i],
}));

/**
 * AS3 convertCoord: linear interpolation from (x1,y1) to (x2,y2) at value x.
 * convertCoord(x1, x2, x, y1, y2)
 */
function convertCoord(x1: number, x2: number, x: number, y1: number, y2: number): number {
  return y1 + (y2 - y1) * (x - x1) / (x2 - x1);
}

/**
 * Cost to learn the next level of a skill (nextLevel = currentLevel + 1, 1-based).
 * AS3: round(convertCoord(1, maxLv[id], currentLv+1, firstCost[id], maxCost[id]))
 */
export function getSkillCost(skillId: number, nextLevel: number): number {
  const spec = SKILL_SPECS[skillId];
  if (!spec) return 0;
  return Math.round(convertCoord(1, spec.maxLevel, nextLevel, spec.firstLevelCost, spec.maxLevelCost));
}

/**
 * Effective value of a skill at a given level.
 * Returns 0 if level < 1.
 * AS3: level<1 ? 0 : round(convertCoord(1, maxLv[id], level, firstVal[id], maxVal[id]))
 */
export function getSkillEffectiveValue(skillId: number, level: number): number {
  if (level < 1) return 0;
  const spec = SKILL_SPECS[skillId];
  if (!spec) return 0;
  return Math.round(convertCoord(1, spec.maxLevel, level, spec.firstLevelValue, spec.maxLevelValue));
}

/**
 * Minimum mage level required to learn the FIRST level of this skill.
 * AS3: reqLvFirst[id] (reqLvInc is 0 for all skills).
 */
export function getSkillPlayerLevelReq(skillId: number): number {
  const spec = SKILL_SPECS[skillId];
  if (!spec) return 0;
  return spec.playerLevelReq;
}

/**
 * Can the player learn one more level of skillId?
 * Checks: mage level >= playerLevelReq, not at maxLevel, has enough points.
 */
export function canLearn(
  skillLevels: Record<number, number>,
  skillId: number,
  mageLevel: number,
  availablePoints: number
): boolean {
  const spec = SKILL_SPECS[skillId];
  if (!spec) return false;
  const current = skillLevels[skillId] ?? 0;
  if (current >= spec.maxLevel) return false;
  if (mageLevel < spec.playerLevelReq) return false;
  const cost = getSkillCost(skillId, current + 1);
  return availablePoints >= cost;
}
